import { type UserModel } from '@/Models/User';
import { createContext, useEffect, useMemo, type ReactNode } from 'react';
import useProvidedContext from '../../Hooks/UseProvidedContext';
import { useAuthenticationContext } from '../App/AuthenticationContext';
import z from 'zod';
import { en, nl, fr, pt, es } from 'zod/locales';

export interface UserContextData extends UserContextState, UserContextActions {}

export interface UserContextState {
    user: UserModel;
}

export interface UserContextActions {
    getUserLanguage: () => string;
}

const setZodErrorMapForLanguage = (language: string): void => {
    const languageWithoutRegion = language.split('-')[0];
    switch (languageWithoutRegion) {
        case 'nl':
            z.config(nl());
            break;
        case 'fr':
            z.config(fr());
            break;
        case 'pt':
            z.config(pt());
            break;
        case 'es':
            z.config(es());
            break;
        default:
            z.config(en());
            break;
    }
};

const createUserContextActions = (user: UserModel): UserContextActions => {
    return {
        getUserLanguage() {
            return user.DisplayLanguage ?? 'en';
        },
    };
};

export const UserContext = createContext<UserContextData | null>(null);

export function UserContextProvider({ children }: Readonly<{ children: ReactNode }>): React.ReactElement {
    const auth = useAuthenticationContext();

    if (!auth.isLoggedIn()) {
        throw new Error('UserContext should only be rendered when user is logged in');
    }

    const user = auth.user as UserModel;

    const userContextActions = useMemo(() => {
        return createUserContextActions(user);
    }, [user]);

    useEffect(() => {
        const userLanguage = userContextActions.getUserLanguage();
        setZodErrorMapForLanguage(userLanguage);
    }, [user.DisplayLanguage]);

    const userContextValue = useMemo(() => ({ user, ...userContextActions }), [user]);

    return (
        <UserContext.Provider value={userContextValue}>
            {children}
        </UserContext.Provider>
    );
}

export const useUserContext = (): UserContextData => useProvidedContext(UserContext);
